import InfoIcon from "@mui/icons-material/Info"
import {
	Box,
	Card,
	CardActions,
	CardContent,
	Grid,
	IconButton,
	Theme,
	Tooltip,
	Typography,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { format, parseISO } from "date-fns"
import { toZonedTime } from "date-fns-tz"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"

import { PlickState } from "../../../../store/actions/file-detail"
import { getBestContrastColor } from "../../../../utils/contrast-color"
import Status from "../../../status"
import PlickDrawer from "../../drawer"

const PlickCard = ({
	plickInfo,
	userTimeZone,
}: {
	plickInfo: PlickState
	userTimeZone: any
}) => {
	const { t } = useTranslation()
	const navigate = useNavigate()

	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)

	const activationDate = parseISO(plickInfo.activationDate)
	const zonedDate = toZonedTime(activationDate, userTimeZone)
	const formattedDate = format(zonedDate, "dd/MM/yyyy")
	const isInError =
		plickInfo.status === "IN_ERROR" || !plickInfo.correctPlick

	const PaydoCard = styled(Card)(({ theme }: { theme: Theme }) => ({
		backgroundColor: getBestContrastColor(theme.palette.primary.main),
		width: "300px",
		margin: "16px",
		boxShadow:
			"0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
	}))

	const CardHeader = styled(Box)(({ theme }: { theme: Theme }) => ({
		display: "flex",
		justifyContent: "space-between",
		color: theme.palette.primary.main,
		marginBottom: "4px",
		alignItems: "flex-start",
		[`& .title`]: {
			color: theme.palette.primary.main,
		},
	}))

	const CardInfo = styled(Typography)(() => ({
		color: "#555",
		marginBottom: "8px",
	}))

	const CardHighlightedInfo = styled(Box)(({ theme }: { theme: Theme }) => ({
		display: "flex",
		justifyContent: "space-between",
		color: theme.palette.primary.main,
		alignItems: "center",
	}))

	const CardFooter = styled(CardActions)(() => ({
		backgroundColor: "#F0F0F0",
		padding: "8px",
		borderRadius: "0 0 8px 8px",
		display: "flex",
		justifyContent: "space-between",
	}))

	const GridContainer = styled(Grid)(() => ({
		display: "flex",
		alignItems: "space-beetween",
		width: "100%",
	}))

	const TextEllipsis = styled(Grid)(() => ({
		overflow: "hidden",
		whiteSpace: "nowrap",
		textOverflow: "ellipsis",
		flexGrow: 1,
		marginRight: 8,
	}))

	const GridDate = styled(Grid)(() => ({
		maxWidth: "85px",
		textAlign: "right",
	}))

	return (
		<>
			<PaydoCard
				sx={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "space-between",
					border: isInError ? "2px solid rgb(211, 47, 47)" : 0,
				}}
			>
				<CardContent>
					<CardHeader>
						<GridContainer container wrap="nowrap">
							<Grid item xs zeroMinWidth>
								<CardInfo className={`title `} variant="body1">
									<Tooltip title={plickInfo.transactionId}>
										<TextEllipsis>{plickInfo.transactionId}</TextEllipsis>
									</Tooltip>
								</CardInfo>
							</Grid>
							<GridDate item>
								<Typography
									component="div"
									variant="subtitle2"
									color="textSecondary"
								>
									<Tooltip title={"Data attivazione: " + formattedDate}>
										<span>{formattedDate}</span>
									</Tooltip>
								</Typography>
							</GridDate>
						</GridContainer>
					</CardHeader>
					<div style={{ marginTop: "-16px", marginBottom: "16px" }}>
						<CardInfo variant="caption">{plickInfo.causal}</CardInfo>
					</div>
					<CardInfo variant="body2">
						<b>{t("card.receiver", { ns: "plick" })}</b>:{" "}
						{plickInfo.receiverName}
					</CardInfo>
					<CardInfo variant="body2">
						<b>{t("card.receiverDevice", { ns: "plick" })}</b>:{" "}
						{plickInfo.receiverDevice}
					</CardInfo>

					<CardHighlightedInfo>
						<Status status={plickInfo.status} />
						<CardInfo sx={{ textAlign: "right" }}>
							{" "}
							<Typography variant="caption">
								<b>{t("card.amount", { ns: "plick" })}</b>
							</Typography>
							<br />€ {plickInfo.amount}{" "}
						</CardInfo>
					</CardHighlightedInfo>
				</CardContent>
				<CardFooter>
					<IconButton color="primary" onClick={() => setIsDrawerOpen(true)}>
						<Tooltip title={t("card.tooltip.detail", { ns: "plick" })}>
							<InfoIcon />
						</Tooltip>
					</IconButton>
				</CardFooter>
			</PaydoCard>
			<PlickDrawer
				currentPlick={plickInfo}
				isDrawerOpen={isDrawerOpen}
				setIsDrawerOpen={setIsDrawerOpen}
			/>
		</>
	)
}

export default PlickCard
